import { api } from '@/lib/api'

export interface CallResponse {
  callId: string;
  meetingId: string;
  roomId: string;
  token: string;
  callType: 'audio' | 'video';
  status: 'pending' | 'active' | 'ended' | 'cancelled';
  initiator: LiveCounselor | LiveStudent;
  participant: LiveCounselor | LiveStudent;
  startedAt?: Date;
  endedAt?: Date;
  duration?: number;
  recordingUrl?: string;
}

export interface LiveStudent {
  id: string;
  name: string;
  fullName: string;
  avatar?: string;
}

export interface LiveCounselor {
  id: string;
  name: string;
  fullName: string;
  avatar?: string;
}

export interface IncomingCallData {
  callId: string;
  meetingId: string;
  roomId: string;
  callType: 'audio' | 'video';
  from: LiveStudent | LiveCounselor;
  sessionId?: string;
}

export interface CallAcceptedData {
  callId: string;
  meetingId: string;
  roomId: string;
  token: string;
  callType?: 'audio' | 'video';
}

export interface CallRejectedData {
  callId: string;
  reason?: string;
}

export interface CallEndedData {
  callId: string;
  reason?: string;
  duration?: number;
  endedBy?: string;
}

export interface MeetingContext {
  meetingId: string;
  token: string;
  callId: string;
  participant?: LiveStudent | LiveCounselor;
  roomId: string;
  callType?: 'audio' | 'video';
}

export interface CreateCallRequest {
  participantId: string;
  callType: 'video' | 'audio';
  sessionId?: string;
  recordMeeting?: boolean;
  maxDuration?: number;
}

export interface JoinCallRequest {
  callId: string;
}

export interface EndCallRequest {
  callId: string;
  reason?: string;
}

export interface RejectCallRequest {
  callId: string;
  reason?: string;
}

/**
 * Creates a new video call
 */
export const createCall = async (request: CreateCallRequest): Promise<CallResponse> => {
  return api.post<CallResponse>('/video-call/create', request);
};

/**
 * Joins an existing call
 */
export const joinCall = async (request: JoinCallRequest): Promise<CallResponse> => {
  return api.post<CallResponse>('/video-call/join', request);
};

/**
 * Ends a call
 */
export const endCall = async (request: EndCallRequest): Promise<CallResponse> => {
  return api.post<CallResponse>('/video-call/end', request);
};

/**
 * Rejects a call
 */
export const rejectCall = async (request: RejectCallRequest): Promise<void> => {
  return api.post<void>('/video-call/reject', request);
};

/**
 * Gets call history
 */
export const getCallHistory = async (limit: number = 20, offset: number = 0): Promise<CallResponse[]> => {
  return api.get<CallResponse[]>('/video-call/history', { limit, offset });
};

/**
 * Gets active call
 */
export const getActiveCall = async (): Promise<CallResponse | null> => {
  return api.get<CallResponse | null>('/video-call/active');
};